import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { AccommodationCategory } from '@graphql/generated-base';
import type { Filters, GuestRating } from '@shared/types/Accommodations.types';

export const VERY_GOOD_RATING: GuestRating = 8;

export type PopularFilterOption = Readonly<{
  id: string;
  filterType: 'cug' | 'userRating' | 'facilities' | 'propertyCategory' | 'freeCancellation';
  getLabel: () => string;
  isActive: (filters: Filters) => boolean;
  toggle: (filters: Filters) => Filters;
}>;

const hasFacility = (facilities: readonly string[], label: string) => facilities.some(f => f.toLowerCase() === label.toLowerCase());

const toggleFacility = (facilities: readonly string[], label: string): readonly string[] => {
  if (hasFacility(facilities, label)) return facilities.filter(f => f.toLowerCase() !== label.toLowerCase());
  const result = [...facilities, label];
  result.sort((a, b) => a.localeCompare(b));
  return result;
};

const facilityOption = (id: string, label: string): Omit<PopularFilterOption, 'getLabel'> => ({
  id,
  filterType: 'facilities',
  isActive: (filters: Filters) => hasFacility(filters.facilities, label),
  toggle: (filters: Filters) => ({ ...filters, facilities: toggleFacility(filters.facilities, label) }),
});

export const usePopularFilterOptions = (): ReadonlyArray<PopularFilterOption> => {
  const { tq } = useTranslation('accommodationsConsole');

  return useMemo(
    (): ReadonlyArray<PopularFilterOption> => [
      {
        id: 'highestDiscount',
        filterType: 'cug',
        getLabel: () => tq`Highest Discount`,
        isActive: (filters: Filters) => filters.cug,
        toggle: (filters: Filters) => ({ ...filters, cug: !filters.cug }),
      },
      {
        id: 'veryGood',
        filterType: 'userRating',
        getLabel: () => tq`Very Good: ${VERY_GOOD_RATING}+`,
        isActive: (filters: Filters) => filters.userRating.includes(VERY_GOOD_RATING),
        toggle: (filters: Filters) => {
          if (filters.userRating.includes(VERY_GOOD_RATING)) {
            return { ...filters, userRating: filters.userRating.filter(r => r !== VERY_GOOD_RATING) };
          }
          const sorted = [...filters.userRating, VERY_GOOD_RATING].sort((a, b) => a - b);
          return { ...filters, userRating: sorted };
        },
      },
      {
        ...facilityOption('parking', tq`Parking`),
        getLabel: () => tq`Parking`,
      },
      {
        ...facilityOption('swimmingPool', tq`Swimming Pool`),
        getLabel: () => tq`Swimming Pool`,
      },
      {
        id: 'hotels',
        filterType: 'propertyCategory',
        getLabel: () => tq`Hotels`,
        isActive: (filters: Filters) => filters.propertyCategories.includes(AccommodationCategory.Hotel),
        toggle: (filters: Filters) => {
          if (filters.propertyCategories.includes(AccommodationCategory.Hotel)) {
            return { ...filters, propertyCategories: filters.propertyCategories.filter(c => c !== AccommodationCategory.Hotel) };
          }
          const sorted = [...filters.propertyCategories, AccommodationCategory.Hotel].sort();
          return { ...filters, propertyCategories: sorted };
        },
      },
      {
        ...facilityOption('breakfastIncluded', tq`Breakfast`),
        getLabel: () => tq`Breakfast Included`,
      },
      {
        id: 'freeCancellation',
        filterType: 'freeCancellation',
        getLabel: () => tq`Free Cancellation`,
        isActive: (filters: Filters) => filters.freeCancellation,
        toggle: (filters: Filters) => ({ ...filters, freeCancellation: !filters.freeCancellation }),
      },
    ],
    [tq],
  );
};
